// DateInput.tsx
import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./DateInput.module.css";
import { CustomDateInput } from "./CustomDateInput";
import ErrorUnit from "components/input/ErrorUnit";

interface DateInputProps {
    /** ISO date string (e.g., "2025-12-06") */
    date: string;
    /** Callback invoked with a formatted date string "YYYY-MM-DD" */
    onDateChange: (newDate: string) => void;
    popperPlacement?: "bottom-end" | "bottom";
    hasMinHeight?: boolean;
    disabled?: boolean;
    selected?: boolean;
    noPastDate?: boolean;
    maxDate?: Date;
    enableError?: boolean;
    errorStr?: string;
}

const DateInput: React.FC<DateInputProps> = ({
    date,
    onDateChange,
    popperPlacement = "bottom-end",
    hasMinHeight = false,
    disabled,
    selected,
    noPastDate = false,
    maxDate,
    enableError,
    errorStr,
}) => {
    return (
        <div className={`${styles.datePickerContainer}`}>
            <DatePicker
                disabled={disabled}
                selected={date ? new Date(`${date}T00:00:00`) : null}
                onChange={(selectedDate) => {
                    if (selectedDate) {
                        // Format the date manually to YYYY-MM-DD without timezone
                        const year = selectedDate.getFullYear();
                        const month = (selectedDate.getMonth() + 1).toString().padStart(2, "0");
                        const day = selectedDate.getDate().toString().padStart(2, "0");
                        const dateWithoutTimezone = `${year}-${month}-${day}`;
                        onDateChange(dateWithoutTimezone);
                    }
                }}
                onClickOutside={() => {
                    const activeElement = document.activeElement as HTMLElement | null;
                    activeElement?.blur(); // Close the picker
                }}
                onSelect={() => {
                    const activeElement = document.activeElement as HTMLElement | null;
                    activeElement?.blur(); // Close the picker after date selection
                }}
                dateFormat="EEE, MMM d" // Format as "Tue, Dec 7"
                placeholderText="Select Date"
                minDate={noPastDate ? new Date() : undefined}
                maxDate={maxDate}
                popperPlacement={popperPlacement} // Align popup to the right
                className={`${styles.dateInput} ${hasMinHeight ? styles.minHeight : ''} ${disabled ? styles.readOnly : ''}`}
                popperClassName={styles.datePickerPopper}
                customInput={<CustomDateInput
                    disabled={disabled}
                    className={`${styles.dateInput} ${hasMinHeight ? styles.minHeight : ''} ${disabled ? styles.readOnly : ''} ${selected ? styles.selected : ''}  ${enableError && errorStr ? styles.errorFocus : ''}`} />}
            />
            {enableError && <ErrorUnit isFocused={false} errorStr={errorStr ?? null} />}
        </div>
    );
};

export default DateInput;
